import type { ApiSuccess } from '@/lib/api'
import type { TenantDetail } from '../types'
import { http } from '@/lib/api'
import { MFA_LOGIN_IS_MANDATORY } from '@/config/app.config'

/** Raw tenant record (swagger/tenants.json → `TenantView`). */
interface TenantView {
  id: string
  name: string
  displayName: string | null
  companyAcn?: string | null
  sector: { id: string; name: string } | null
  status: 'active' | 'inactive' | 'suspended'
  reportingGroup?: boolean
  branchEntities?: Array<{ id: string; name: string; isLead?: boolean }>
  ssoLoginEnabled?: boolean
  mfaLoginEnabled?: boolean
}

function mapTenant(t: TenantView): TenantDetail {
  return {
    id: t.id,
    name: t.name,
    displayName: t.displayName,
    companyAcn: t.companyAcn ?? null,
    sector: t.sector,
    status: t.status,
    reportingGroup: t.reportingGroup ?? false,
    branchEntities: (t.branchEntities ?? []).map((b) => ({
      id: b.id,
      name: b.name,
      isLead: b.isLead ?? false,
    })),
    // Backend defaults both login methods to enabled.
    ssoLoginEnabled: t.ssoLoginEnabled ?? true,
    // Mandatory — a legacy `false` from the API is ignored (see AMLIQ-264).
    mfaLoginEnabled: MFA_LOGIN_IS_MANDATORY ? true : (t.mfaLoginEnabled ?? true),
  }
}

export const tenantApi = {
  /**
   * Fetch full tenant detail (reporting group + branch offices).
   * See swagger/tenants.json → `GET /admin/tenants/{id}`.
   *
   * Self-service updates to the tenant live in the account-settings feature
   * (`features/account-settings/api/account-settings.api.ts`) — auth only
   * reads the tenant for the session.
   */
  detail: (id: string): Promise<TenantDetail> =>
    http.get<ApiSuccess<TenantView>>(`/admin/tenants/${id}`).then((res) => mapTenant(res.data)),
}
